// ═══ 데스크톱 상세 패널 검차 정보 — 공유 리포트 인라인판 (신고 371 §9) ═══
// 정본 = routes/eval-photos.js renderReportPage. 섹션·순서·판정 규칙을 그대로 따른다:
//   종합 점수(0 은 행 생략, 전부 0 이면 섹션 생략) → 도색 상태(범례 + 3면도 + 11부위)
//   → 사고·수리 이력(4면도 항상 + 차량 분류 칩 + 부위별 과거/현재) → 메모 4종(값 있을 때만).
// 데스크톱 패널(layout='pane') 전용 — 모바일 페이지는 앱 바이어 화면 그대로다(§8.1).
// 색·지오메트리·legacy 정규화는 components/EvalCarDiagram.js 재사용(v2/ hex 리터럴 0 가드).
// props: { result, locale, orderId }
import React from 'react';
import { T, R, FONT } from '../../../theme';
import { useT } from '../../../../i18n/I18nProvider';
import { PAINT_PARTS, evalPaintColor, normalizeDamageEntry,
  damageEntryFor, typeByKey, PAINT_TO_ZONE } from '../../../../components/EvalCarDiagram';
import LtrText from '../../parts/LtrText';
import SimpleCard from '../../parts/SimpleCard';
import CarDiagram from './inspection/CarDiagram';
import NoteBlock from './inspection/NoteBlock';
import { partLabel, paintPartLabel, damageTypeLabel, flagLabel, parseDamage, paintValue } from './inspection/domain';

// 리포트 SCORE_PARTS 와 같은 순서. 하부 점수는 실데이터에 >0 이 있어 행이 뜬다(V3 실측) —
// ARB 에 전용 키가 없어 사진 분류 키(evalCatUndercarriage='하부엔진')를 빌려 쓴다(신규 문구 0 원칙, Jin 승인 대기).
var SCORES = [
  ['score_overall', 'evalOverall'], ['score_exterior', 'evalScoreExterior'],
  ['score_interior', 'evalScoreInterior'], ['score_engine', 'evalScoreEngine'],
  ['score_undercarriage', 'evalCatUndercarriage']
];
// 리포트 NOTE_FIELDS 와 같은 순서 · 같은 ?field= 값.
var NOTES = [
  ['inspector_notes', 'evalNotesTitle'], ['wheel_notes', 'evalWheelNotesLabel'],
  ['tire_notes', 'evalTireNotesLabel'], ['body_notes', 'evalBodyNotesLabel']
];
var PAINT_VIEWS = ['left', 'top', 'right'];
var DAMAGE_VIEWS = ['left', 'top', 'bottom', 'right'];

var stack = { display: 'flex', flexDirection: 'column', gap: 11 };
var pad = '12px 14px';
var rowsBox = { display: 'flex', flexDirection: 'column', gap: 2 };
// 부위명이 긴 로케일(ar)에서 이름을 자르는 대신 값 쪽을 아랫줄로 흘린다.
var row = { display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', flexWrap: 'wrap' };
var name = { fontSize: 12, fontWeight: 600, color: T.body };
var valueCell = { marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: 6, flex: 'none' };
var stars = { fontSize: 13, letterSpacing: '1px' };
var scoreNum = { fontSize: 12.5, fontWeight: 800, color: T.ink };
var legend = { display: 'flex', flexWrap: 'wrap', gap: '5px 12px', marginBottom: 9 };
var legendItem = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: FONT.label, fontWeight: 600, color: T.muted };
var dot = { width: 8, height: 8, borderRadius: R.pill, flex: 'none' };
var grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(178px, 1fr))', gap: '2px 14px', marginTop: 11 };
var chips = { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 11 };
var flagChip = { display: 'inline-flex', alignItems: 'center', fontSize: FONT.label, fontWeight: 700,
  color: T.warn, background: T.warnTint, border: '1px solid ' + T.warnBd, borderRadius: R.pill, padding: '3px 10px' };

// 리포트와 같은 별 5개(U+2605) — 켜짐/꺼짐은 색으로만 구분한다(문자 교체 없음).
function starRow(value) {
  var on = Math.max(0, Math.min(5, value));
  return (<LtrText style={stars}>
    <span style={{ color: T.warn }}>{new Array(on + 1).join('\u2605')}</span>
    <span style={{ color: T.line }}>{new Array(6 - on).join('\u2605')}</span>
  </LtrText>);
}

function typeChip(t, type, key) {
  return (<span key={key} data-damage-code={type.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: FONT.label, fontWeight: 700, color: type.fg, background: type.bg, borderRadius: R.pill, padding: '3px 9px' }}>
    <LtrText>{type.short}</LtrText> {damageTypeLabel(t, type.key)}
  </span>);
}

function ScoreSection(props) {
  var t = props.t;
  var rows = SCORES.filter(function (s) { return Number(props.result[s[0]]) > 0; });
  if (!rows.length) return null;
  return (<SimpleCard title={t('evalOverall')} pad={pad}>
    <div data-section="score" style={rowsBox}>
      {rows.map(function (s) {
        var v = Number(props.result[s[0]]);
        return (<div key={s[0]} data-score={s[0]} style={row}>
          <span style={name}>{t(s[1])}</span>
          <span style={valueCell}>
            {starRow(v)}
            <LtrText style={scoreNum}>{v + '/5'}</LtrText>
          </span>
        </div>);
      })}
    </div>
  </SimpleCard>);
}

function PaintSection(props) {
  var t = props.t;
  var result = props.result;
  var byZone = {};
  PAINT_PARTS.forEach(function (p) { byZone[PAINT_TO_ZONE[p[0]]] = paintValue(result, p[0]); });
  var resolveColor = function (zoneKey) {
    var v = byZone[zoneKey];
    return v == null ? null : evalPaintColor(v);
  };
  var legendRow = [['evalPaintLegendNormal', 0], ['evalPaintLegendPainted', 200],
    ['evalPaintLegendSheetMetal', 300], ['evalPaintUnmeasured', null]];
  return (<SimpleCard title={t('evalPaintTitle')} pad={pad}>
    <div data-section="paint">
      <div style={legend}>
        {legendRow.map(function (l) {
          return (<span key={l[0]} style={legendItem}>
            <i style={Object.assign({ background: evalPaintColor(l[1]) }, dot)} />{t(l[0])}
          </span>);
        })}
      </div>
      <CarDiagram t={t} views={PAINT_VIEWS} resolveColor={resolveColor} />
      <div style={grid}>
        {PAINT_PARTS.map(function (p) {
          var v = paintValue(result, p[0]);
          return (<div key={p[0]} data-paint={p[0]} style={row}>
            <span style={name}>{paintPartLabel(t, p[0])}</span>
            <span style={valueCell}>
              {v === null
                ? <LtrText style={{ color: T.faint, fontWeight: 700 }}>-</LtrText>
                : v > 0
                  ? <LtrText style={{ color: evalPaintColor(v), fontWeight: 800 }}>{v + ' µm'}</LtrText>
                  : <span style={{ fontSize: 11.5, fontWeight: 700, color: evalPaintColor(0) }}>{t('evalPaintNormal')}</span>}
            </span>
          </div>);
        })}
      </div>
    </div>
  </SimpleCard>);
}

function DamageSection(props) {
  var t = props.t;
  var damage = parseDamage(props.result.damage_data);
  var flags = Array.isArray(props.result.vehicle_flags) ? props.result.vehicle_flags : [];
  var rows = Object.keys(damage).map(function (key) {
    var e = normalizeDamageEntry(damage[key]);
    var past = e.past ? typeByKey(e.past) : null;
    var curr = e.current ? typeByKey(e.current) : null;
    return (past || curr) ? { key: key, past: past, curr: curr } : null;
  }).filter(Boolean);
  // 도면 채색·기호는 현재 상태 우선, 없으면 과거 이력 (리포트 dmgEntry 와 동일).
  var entryOf = function (zoneKey) {
    var e = damageEntryFor(damage, zoneKey);
    return typeByKey(e.current) || typeByKey(e.past);
  };
  return (<SimpleCard title={t('evalDamageTitle', { count: rows.length })} pad={pad}>
    <div data-section="damage">
      <CarDiagram t={t} views={DAMAGE_VIEWS}
        resolveColor={function (k) { var e = entryOf(k); return e ? e.fg : null; }}
        resolveSymbol={function (k) { var e = entryOf(k); return e ? e.short : null; }} />
      {flags.length ? (<div style={chips}>
        {flags.map(function (f) { return <span key={f} data-flag={f} style={flagChip}>{flagLabel(t, f)}</span>; })}
      </div>) : null}
      {rows.length ? (<div style={grid}>
        {rows.map(function (r) {
          return (<div key={r.key} data-damage={r.key} style={row}>
            <span style={name}>{partLabel(t, r.key)}</span>
            <span style={valueCell}>
              {r.past ? typeChip(t, r.past, 'p') : null}
              {r.curr ? typeChip(t, r.curr, 'c') : null}
            </span>
          </div>);
        })}
      </div>) : null}
    </div>
  </SimpleCard>);
}

function InspectionSections(props) {
  var t = useT();
  var result = props.result;
  if (!result) return null;
  return (<div className="ss-inspection" style={stack}>
    <ScoreSection t={t} result={result} />
    <PaintSection t={t} result={result} />
    <DamageSection t={t} result={result} />
    {NOTES.map(function (n) {
      var text = typeof result[n[0]] === 'string' ? result[n[0]].trim() : '';
      if (!text) return null;
      return <NoteBlock key={n[0] + ':' + props.locale} title={t(n[1])} text={text} field={n[0]}
        orderId={props.orderId} locale={props.locale} />;
    })}
  </div>);
}

export default InspectionSections;
